import Keycloak from 'keycloak-js';
import config from 'config';

let keycloakClient = null;
let keycloakInitPromise = null;
let keycloakLoginInProgress = false;
let keycloakSessionSyncPromise = null;
let keycloakSessionEnsurePromise = null;

const KEYCLOAK_MIN_VALIDITY_SECONDS = 30;

function buildRedirectUri(path = '/app/dashboard') {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${window.location.origin}${cleanPath}`;
}

function resetKeycloakRuntimeState() {
    keycloakLoginInProgress = false;
    keycloakSessionSyncPromise = null;
    keycloakSessionEnsurePromise = null;
    keycloakInitPromise = null;
}

function getKeycloakSettings() {
    const url = process.env.REACT_APP_KEYCLOAK_URL;
    const realm = process.env.REACT_APP_KEYCLOAK_REALM;
    const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

    if (!url || !realm || !clientId) {
        return null;
    }

    return { url, realm, clientId };
}

export function isKeycloakEnabled() {
    return config.allowKeycloakAuth !== false;
}

export function isKeycloakConfigured() {
    return !!getKeycloakSettings();
}

function getOrCreateClient() {
    if (!isKeycloakEnabled()) {
        return null;
    }

    if (keycloakClient) {
        return keycloakClient;
    }

    const settings = getKeycloakSettings();
    if (!settings) {
        console.warn('Keycloak is enabled but environment variables are missing.');
        return null;
    }

    keycloakClient = new Keycloak(settings);
    return keycloakClient;
}

async function ensureKeycloakInitialized() {
    const client = getOrCreateClient();
    if (!client) {
        return false;
    }

    if (!keycloakInitPromise) {
        keycloakInitPromise = client
            .init({
                onLoad: 'check-sso',
                pkceMethod: 'S256',
                checkLoginIframe: false
            })
            .catch(() => false);
    }

    return await keycloakInitPromise;
}

async function exchangeKeycloakToken(kcToken) {
    const response = await fetch('/api/keycloak/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: kcToken })
    });

    return await response.json();
}

function updateLocalStorageUser(result) {
    if (result.error === 'OK' && result.user) {
        localStorage.user = JSON.stringify(result.user);
        localStorage.token = result.session;
        localStorage.role = result.user.role?.name || '';
        localStorage.systemRole = result.user.role?.systemRole || '';
    }

    if (result.error === 'OK' && result.session) {
        localStorage.token = result.session;
    }
}

export async function syncLocalStorageFromKeycloak() {
    const client = getOrCreateClient();
    if (!client || !client.token) {
        return false;
    }

    if (keycloakSessionSyncPromise) {
        return await keycloakSessionSyncPromise;
    }

    keycloakSessionSyncPromise = (async () => {
        const result = await exchangeKeycloakToken(client.token);
        updateLocalStorageUser(result);
        return result.error === 'OK';
    })();

    try {
        return await keycloakSessionSyncPromise;
    } finally {
        keycloakSessionSyncPromise = null;
    }
}

export async function ensureLegacySessionFromKeycloak(force = false) {
    if (!force && localStorage.token) {
        return true;
    }

    if (!force && keycloakSessionEnsurePromise) {
        return await keycloakSessionEnsurePromise;
    }

    const runEnsure = async () => {
        const client = getOrCreateClient();
        if (!client) {
            return false;
        }

        await ensureKeycloakInitialized();

        if (!client.authenticated) {
            return false;
        }

        await client.updateToken(KEYCLOAK_MIN_VALIDITY_SECONDS);

        if (!force && localStorage.token) {
            return true;
        }

        return await syncLocalStorageFromKeycloak();
    };

    if (force) {
        return await runEnsure();
    }

    keycloakSessionEnsurePromise = runEnsure();
    try {
        return await keycloakSessionEnsurePromise;
    } finally {
        keycloakSessionEnsurePromise = null;
    }
}

export async function initializeKeycloakSession() {
    const client = getOrCreateClient();
    if (!client) {
        return false;
    }

    const authenticated = await ensureKeycloakInitialized();

    if (authenticated) {
        return await ensureLegacySessionFromKeycloak(!localStorage.token);
    }

    return false;
}

export async function startKeycloakLogin(path = '/app/dashboard') {
    const client = getOrCreateClient();
    if (!client) {
        throw new Error('KEYCLOAK_NOT_CONFIGURED');
    }

    await ensureKeycloakInitialized();

    if (!client.authenticated) {
        if (keycloakLoginInProgress) {
            return false;
        }
        keycloakLoginInProgress = true;
        try {
            await client.login({ redirectUri: buildRedirectUri(path) });
        } finally {
            keycloakLoginInProgress = false;
        }
        return false;
    }

    keycloakLoginInProgress = false;

    return await ensureLegacySessionFromKeycloak(true);
}

export async function refreshKeycloakToken(
    minValidity = KEYCLOAK_MIN_VALIDITY_SECONDS
) {
    const client = getOrCreateClient();
    if (!client || !client.authenticated) {
        return false;
    }

    await client.updateToken(minValidity);
    await syncLocalStorageFromKeycloak();
    return true;
}

export async function logoutFromKeycloak(
    path = '/auth/login',
    { globalLogout = true } = {}
) {
    const client = getOrCreateClient();

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('systemRole');

    if (!client) {
        resetKeycloakRuntimeState();
        return;
    }

    if (globalLogout) {
        resetKeycloakRuntimeState();
        await client.logout({ redirectUri: buildRedirectUri(path) });
        return;
    }

    // Local app logout without redirecting user to IdP logout page.
    if (typeof client.clearToken === 'function') {
        client.clearToken();
    }

    resetKeycloakRuntimeState();
}
